import { useQuery } from '@apollo/client';
import type { SafetyAction, WorkflowType } from '@communities/content-management/domain';
import { GET_COMMUNITY_ACTIONS } from '@communities/content-management/graphql';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain/errors';
import { useMemo } from 'react';

interface CommunityActionsResponse {
  community: {
    id: string;
    actions: SafetyAction[];
  } | null;
}

export function useCommunityActions(communityId?: string, workflowType?: WorkflowType) {
  const { data, loading, error, refetch } = useQuery<CommunityActionsResponse>(GET_COMMUNITY_ACTIONS, {
    variables: { communityId },
    fetchPolicy: 'cache-first',
    skip: !communityId,
  });

  const errors: DomainError[] | null = useMemo(() => {
    if (!error) return null;

    return transformErrors(error);
  }, [error]);

  // Filter actions by workflowType if provided (case-insensitive)
  // Shows legacy actions without workflowType on all tabs for backward compatibility
  const actions: SafetyAction[] = useMemo(() => {
    const allActions: SafetyAction[] = data?.community?.actions || [];

    if (!workflowType) {
      return allActions;
    }

    return allActions.filter(action => !action.workflowType || action.workflowType.toLowerCase() === workflowType.toLowerCase());
  }, [data?.community?.actions, workflowType]);

  return {
    actions,
    loading,
    errors,
    refetch,
  };
}

export default useCommunityActions;
